import React, { useState, useEffect } from 'react';
import { Modal, Radio, Input, Button, message } from 'antd';
import { pauseBudget } from 'api/marketing';
import { WarningOutlined } from '@ant-design/icons';
import s from './s.module.less';

interface PauseProListingsModalProps {
    visible: boolean;
    onCancel: () => void;
    onConfirm?: () => void;
}

const PauseProListingsModal: React.FC<PauseProListingsModalProps> = ({
    visible,
    onCancel,
    onConfirm
}) => {
    const [selectedReason, setSelectedReason] = useState<string>('');
    const [customReason, setCustomReason] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const handleConfirm = async () => {
        if (!selectedReason) return;

        try {
            const reason = selectedReason === 'other' ? customReason : selectedReason;
            setLoading(true);
            await pauseBudget({ reason });
            onConfirm?.();
            onCancel();
            message.success('Your Pro-Listings has been paused');
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setSelectedReason('');
        setCustomReason('');
        onCancel();
    };

    const isConfirmDisabled = !selectedReason || (selectedReason === 'other' && !customReason.trim());

    useEffect(() => {
        if (!visible) {
            setSelectedReason('');
            setCustomReason('');
        }
    }, [visible]);

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={543}
            className={s.modal}
            title="Pause Pro-Listings"
        >
            <div className={s.content}>
                <div className={s.description}>
                    Do you want to pause your Pro-Listings? Your profile will stop appearing
                    in sponsored placements, and charges will pause until you resume.
                </div>

                <div className={s.warningSection}>
                    <div className={s.warningHeader}>
                        <WarningOutlined className={s.warningIcon} />
                        <span className={s.warningTitle}>Before you pause, please note:</span>
                    </div>
                    <ul className={s.warningList}>
                        <li>New patient bookings will be paused</li>
                        <li>Existing appointments won&apos;t be affected</li>
                        <li>Your search ranking may decrease while paused</li>
                        <li>Competitors may gain more visibility during your pause</li>
                    </ul>
                </div>

                <div className={s.reasonSection}>
                    <Radio.Group
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                        className={s.radioGroup}
                    >
                        <div className={s.radioOption}>
                            <Radio value="The cost is too high">The cost is too high</Radio>
                        </div>
                        <div className={s.radioOption}>
                            <Radio value="No additional bookings are needed at the moment">No additional bookings are needed at the moment</Radio>
                        </div>
                        <div className={s.radioOption}>
                            <Radio value="Not seeing enough bookings">Not seeing enough bookings</Radio>
                        </div>
                        <div className={s.radioOption}>
                            <Radio value="other">
                                <span>Other reasons</span>
                                <span className={s.inputLabel}>(need to input)</span>
                            </Radio>
                        </div>
                    </Radio.Group>

                    {selectedReason === 'other' && (
                        <div className={s.customReasonInput}>
                            <Input.TextArea
                                value={customReason}
                                onChange={(e) => setCustomReason(e.target.value)}
                                placeholder="Specify your reason for canceling this appointment..."
                                rows={4}
                                className={s.textArea}
                            />
                        </div>
                    )}
                </div>

                <div className={s.footer}>
                    <Button
                        onClick={handleCancel}
                        className={s.cancelButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleConfirm}
                        disabled={isConfirmDisabled}
                        className={s.confirmButton}
                        loading={loading}
                    >
                        Pause
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default PauseProListingsModal;
